import { gql, useMutation, useQuery } from '@apollo/client'
import CheckIcon from '@mui/icons-material/Check'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DownloadIcon from '@mui/icons-material/Download'
import InstagramIcon from '@mui/icons-material/Instagram'
import IosShareIcon from '@mui/icons-material/IosShare'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Link as MuiLink,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Title, useTranslate } from 'react-admin'
import { useLocation, useSearchParams } from 'react-router-dom'
import { fileStorage, fileUrl, zip } from '~/helpers'
import { caption, coverText, type Occasion, type SocialBoat } from './caption'
import { PUBLISH, useInstagram } from './instagram'
import { InstagramAccount } from './InstagramAccount'
import {
  type Area,
  drawSlide,
  fetchBitmap,
  type Format,
  FORMATS,
  loadFont,
  loadImage,
  toJpeg,
} from './render'

/**
 * "Social Media": a ready-to-post kit for one boat — up to ten slides in an
 * Instagram format (the first with name, facts and price), and the caption.
 * Downloaded as a ZIP, on a phone handed straight to the share sheet, or
 * published to the connected Instagram account: the slides go to the public
 * `social-posts` bucket, the feed service hands their URLs to Instagram
 * (instagram_publish), and the CMS deletes them again. The slides themselves
 * are drawn in the browser.
 */

const BOATS = gql`
  query SocialBoats {
    boats(order_by: [{ name: asc }, { id: asc }]) {
      id
      name
      manufacturer
      model
    }
  }
`

const BOAT = gql`
  query SocialBoat($id: Int!) {
    boat: boats_by_pk(id: $id) {
      id
      name
      manufacturer
      model
      build_year
      boat_type
      length_total
      width
      draft_max
      asking_price
      offer_price
      location_town
      translated_marketing_description
      status
      photos: attachments(
        where: { content_type: { _like: "image/%" } }
        order_by: [{ _order: asc }, { id: asc }]
      ) {
        id
        uri
        logo_box
      }
    }
    office: nautix_settings_by_pk(id: 1) {
      office_phone
      office_email
    }
  }
`

const MAX_SLIDES = 10
const MAX_CAPTION = 2200
const LOGO_KEY = 'photoStudio.logo'
const DEFAULT_LOGO = `${import.meta.env.BASE_URL}android-chrome-512x512.png`
const OCCASIONS: Occasion[] = ['new', 'price', 'sold', 'none']

interface BoatOption {
  id: number
  name: string | null
  manufacturer: string | null
  model: string | null
}

/** "Name – Maker Model", once when they are the same. */
const boatLabel = (b: BoatOption) => {
  const makeModel = [b.manufacturer, b.model].filter(Boolean).join(' ')
  const parts = [b.name, makeModel].filter(Boolean) as string[]
  if (parts.length === 2 && parts[0].trim().toLowerCase() === parts[1].trim().toLowerCase()) {
    return parts[0]
  }
  return parts.join(' – ') || `#${b.id}`
}

const slug = (text: string) =>
  text
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase() || 'boot'

/** The photo page's logo, if someone chose their own there. */
const logoSource = () => {
  try {
    return localStorage.getItem(LOGO_KEY) ?? DEFAULT_LOGO
  } catch {
    return DEFAULT_LOGO
  }
}

interface Slide {
  name: string
  blob: Blob
  url: string
}

export const SocialPost = () => {
  const translate = useTranslate()
  const [params, setParams] = useSearchParams()
  const boatId = Number(params.get('boat')) || null

  const { data: list } = useQuery<{ boats: BoatOption[] }>(BOATS)
  const { data, loading } = useQuery(BOAT, {
    variables: { id: boatId },
    skip: !boatId,
  })
  // logo_box: where the logo already is, on photos from "Bilder aufbereiten".
  const boat:
    (SocialBoat & { photos: { id: number; uri: string; logo_box: Area | null }[] }) | null =
    data?.boat ?? null

  const [occasion, setOccasion] = useState<Occasion>('new')
  const [format, setFormat] = useState<Format>('post')
  const [logoOnAll, setLogoOnAll] = useState(true)
  const [selected, setSelected] = useState<number[]>([])
  const [thumbs, setThumbs] = useState<Record<number, string>>({})
  const [slides, setSlides] = useState<Slide[]>([])
  const [rendering, setRendering] = useState(false)
  const [text, setText] = useState('')
  const [message, setMessage] = useState<{
    severity: 'success' | 'error'
    text: string
    link?: string | null
  } | null>(null)
  const bitmaps = useRef(new Map<number, ImageBitmap>())
  const instagram = useInstagram()
  const [publishMutation] = useMutation(PUBLISH)
  const [confirming, setConfirming] = useState(false)
  const [phase, setPhase] = useState<'uploading' | 'publishing' | null>(null)
  const location = useLocation()

  // Back from connecting (/social#instagram): show the account.
  useEffect(() => {
    if (location.hash === '#instagram' && instagram.loaded)
      document.getElementById('instagram')?.scrollIntoView({ behavior: 'smooth' })
  }, [location.hash, instagram.loaded])

  // A new boat: its first ten photos, in gallery order, and fresh thumbnails.
  useEffect(() => {
    bitmaps.current.clear()
    setSlides([])
    setThumbs({})
    setSelected(boat?.photos.slice(0, MAX_SLIDES).map((p) => p.id) ?? [])
    let current = true
    ;(async () => {
      for (const photo of boat?.photos ?? []) {
        const url = await fileUrl(photo.uri).catch(() => null)
        if (!current) return
        if (url) setThumbs((t) => ({ ...t, [photo.id]: url }))
      }
    })()
    return () => {
      current = false
    }
  }, [boat?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // The caption follows the boat and the occasion; edits are the user's until then.
  useEffect(() => {
    if (boat)
      setText(
        caption(boat, occasion, {
          phone: data?.office?.office_phone,
          email: data?.office?.office_email,
        })
      )
  }, [boat?.id, occasion, data?.office]) // eslint-disable-line react-hooks/exhaustive-deps

  // The slides, redrawn when anything that shows on them changes.
  useEffect(() => {
    if (!boat || selected.length === 0) {
      setSlides([])
      return
    }
    let current = true
    const made: Slide[] = []
    setRendering(true)
    ;(async () => {
      try {
        await loadFont()
        const logo = await loadImage(logoSource()).catch(() => null)
        const base = slug(boatLabel(boat as BoatOption))
        for (let i = 0; i < selected.length; i++) {
          const photo = boat.photos.find((p) => p.id === selected[i])
          if (!photo) continue
          let bitmap = bitmaps.current.get(photo.id)
          if (!bitmap) {
            const url = await fileUrl(photo.uri)
            if (!url) continue
            bitmap = await fetchBitmap(url)
            bitmaps.current.set(photo.id, bitmap)
          }
          if (!current) return
          const canvas = drawSlide({
            image: bitmap,
            format,
            // A photo that has the logo already gets no second one.
            logo: !photo.logo_box && (i === 0 || logoOnAll) ? logo : null,
            cover: i === 0 ? coverText(boat, occasion) : undefined,
            keep: photo.logo_box,
          })
          const blob = await toJpeg(canvas)
          made.push({
            name: `${base}-${String(i + 1).padStart(2, '0')}.jpg`,
            blob,
            url: URL.createObjectURL(blob),
          })
        }
        if (current) setSlides(made)
      } catch (e: any) {
        if (current) setMessage({ severity: 'error', text: e.message })
      } finally {
        if (current) setRendering(false)
      }
    })()
    return () => {
      current = false
    }
  }, [boat, selected, format, occasion, logoOnAll])

  // Object URLs of replaced slides are let go.
  useEffect(() => () => slides.forEach((s) => URL.revokeObjectURL(s.url)), [slides])

  const toggle = (id: number) =>
    setSelected((s) =>
      s.includes(id) ? s.filter((x) => x !== id) : s.length >= MAX_SLIDES ? s : [...s, id]
    )

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setMessage({ severity: 'success', text: translate('social.copied') })
    } catch {
      setMessage({ severity: 'error', text: translate('social.copy_failed') })
    }
  }

  const download = async () => {
    const entries = [
      ...slides.map((s) => ({ name: s.name, data: s.blob })),
      { name: 'beitrag.txt', data: new Blob([text], { type: 'text/plain' }) },
    ]
    const archive = await zip(entries)
    const url = URL.createObjectURL(archive)
    const a = document.createElement('a')
    a.href = url
    a.download = `${slug(boatLabel(boat as BoatOption))}-social.zip`
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 60_000)
    setMessage({
      severity: 'success',
      text: translate('social.downloaded', { smart_count: slides.length }),
    })
  }

  const files = useMemo(
    () => slides.map((s) => new File([s.blob], s.name, { type: 'image/jpeg' })),
    [slides]
  )
  const canShare =
    files.length > 0 && typeof navigator.canShare === 'function' && navigator.canShare({ files })

  // A story has no caption; a post is one carousel.
  const kind = format === 'story' ? 'story' : 'feed'

  const publish = async () => {
    setConfirming(false)
    setMessage(null)
    const uploaded: string[] = []
    let answered = false
    try {
      setPhase('uploading')
      for (const file of files) uploaded.push((await fileStorage.put(file, 'social-posts')).key)
      setPhase('publishing')
      const { data } = await publishMutation({
        variables: {
          kind,
          file_ids: uploaded,
          caption: kind === 'feed' ? text : null,
          boat_id: boat?.id ?? null,
        },
      })
      answered = true
      const result = data?.instagram_publish
      setMessage(
        result?.ok
          ? {
              severity: 'success',
              text: translate('social.published', { message: result.message }),
              link: result.permalink,
            }
          : { severity: 'error', text: result?.message ?? 'Instagram' }
      )
    } catch (e: any) {
      answered = !!e?.graphQLErrors?.length
      setMessage({ severity: 'error', text: e.message })
    } finally {
      setPhase(null)
      // Instagram has its own copy now, or took none. Without an answer it may
      // still be fetching them; those few stay in the bucket.
      if (answered) await Promise.all(uploaded.map((id) => fileStorage.remove(id).catch(() => {})))
      instagram.refetch()
    }
  }

  const share = async () => {
    // Instagram ignores shared text; the caption goes to the clipboard first,
    // to paste there.
    await navigator.clipboard?.writeText(text).catch(() => {})
    try {
      await navigator.share({ files, text })
    } catch (e: any) {
      if (e?.name !== 'AbortError') setMessage({ severity: 'error', text: e.message })
    }
  }

  const options = list?.boats ?? []
  const value = options.find((b) => b.id === boatId) ?? null
  const { width, height } = FORMATS[format]

  return (
    <Box sx={{ p: 2, minWidth: 0, maxWidth: '100%' }}>
      <Title title={translate('social.title')} />
      <Typography variant='body1' color='text.secondary' sx={{ mb: 2, maxWidth: 900 }}>
        {translate('social.intro')}
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, md: 5 }}>
          <Autocomplete
            options={options}
            value={value}
            getOptionLabel={boatLabel}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_, b) => setParams(b ? { boat: String(b.id) } : {})}
            renderInput={(p) => <TextField {...p} label={translate('social.boat')} />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TextField
            select
            fullWidth
            label={translate('social.occasion')}
            value={occasion}
            onChange={(e) => setOccasion(e.target.value as Occasion)}
          >
            {OCCASIONS.map((o) => (
              <MenuItem key={o} value={o}>
                {translate(`social.occasions.${o}`)}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <ToggleButtonGroup
            exclusive
            fullWidth
            value={format}
            onChange={(_, f) => f && setFormat(f)}
            aria-label={translate('social.format')}
            sx={{ height: '100%' }}
          >
            {(Object.keys(FORMATS) as Format[]).map((f) => (
              <ToggleButton key={f} value={f}>
                {translate(`social.formats.${f}`)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Grid>
      </Grid>

      {message && (
        <Alert
          severity={message.severity}
          sx={{ mb: 2 }}
          onClose={() => setMessage(null)}
          data-testid='social-message'
        >
          {message.text}
          {message.link && (
            <>
              {' '}
              <MuiLink href={message.link} target='_blank' rel='noopener'>
                {translate('social.view_post')}
              </MuiLink>
            </>
          )}
        </Alert>
      )}

      {!boatId && <Alert severity='info'>{translate('social.choose_boat')}</Alert>}
      {boatId && loading && <CircularProgress />}
      {boat && boat.photos.length === 0 && (
        <Alert severity='warning'>{translate('social.no_photos')}</Alert>
      )}

      {boat && boat.photos.length > 0 && (
        <Grid container spacing={2} sx={{ maxWidth: '100%' }}>
          <Grid size={{ xs: 12 }} sx={{ minWidth: 0 }}>
            <Card variant='outlined'>
              <CardContent>
                <Stack
                  direction='row'
                  sx={{ mb: 1, justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Typography variant='h6'>
                    {translate('social.photos', { count: selected.length, max: MAX_SLIDES })}
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={logoOnAll}
                        onChange={(e) => setLogoOnAll(e.target.checked)}
                      />
                    }
                    label={translate('social.logo_on_all')}
                  />
                </Stack>
                <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                  {translate('social.photos_hint')}
                  {boat.photos.some((p) => p.logo_box) && ` ${translate('social.logo_hint')}`}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {boat.photos.map((p) => {
                    const index = selected.indexOf(p.id)
                    return (
                      <Box
                        key={p.id}
                        role='button'
                        aria-pressed={index >= 0}
                        data-testid='social-photo'
                        onClick={() => toggle(p.id)}
                        sx={{
                          position: 'relative',
                          width: 120,
                          height: 90,
                          borderRadius: 1,
                          overflow: 'hidden',
                          cursor: 'pointer',
                          outline: index >= 0 ? '3px solid' : '1px solid',
                          outlineColor: index >= 0 ? 'primary.main' : 'divider',
                          opacity: index >= 0 ? 1 : 0.55,
                          bgcolor: 'grey.200',
                        }}
                      >
                        {thumbs[p.id] && (
                          <img
                            src={thumbs[p.id]}
                            alt=''
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        )}
                        {index >= 0 && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 4,
                              left: 4,
                              minWidth: 22,
                              height: 22,
                              px: 0.5,
                              borderRadius: 11,
                              bgcolor: 'primary.main',
                              color: 'primary.contrastText',
                              fontSize: 13,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {index + 1}
                          </Box>
                        )}
                      </Box>
                    )
                  })}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, lg: 7 }} sx={{ minWidth: 0 }}>
            <Card variant='outlined' sx={{ minWidth: 0 }}>
              <CardContent>
                <Stack direction='row' spacing={1} sx={{ mb: 1, alignItems: 'center' }}>
                  <Typography variant='h6'>{translate('social.preview')}</Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {width} × {height}
                  </Typography>
                  {rendering && <CircularProgress size={18} />}
                </Stack>
                <Box
                  // Scrolls on its own: without containment its full width (all
                  // slides side by side) would widen the whole page.
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    overflowX: 'auto',
                    pb: 1,
                    width: '100%',
                    contain: 'inline-size',
                  }}
                  data-testid='social-slides'
                >
                  {slides.map((s) => (
                    <Box
                      key={s.url}
                      component='img'
                      src={s.url}
                      alt={s.name}
                      data-testid='social-slide'
                      sx={{
                        height: 360,
                        aspectRatio: `${width} / ${height}`,
                        borderRadius: 1,
                        boxShadow: 1,
                        flex: 'none',
                      }}
                    />
                  ))}
                </Box>
                <Stack direction='row' spacing={1} sx={{ mt: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant='contained'
                    startIcon={<DownloadIcon />}
                    disabled={rendering || slides.length === 0}
                    onClick={download}
                  >
                    {translate('social.download')}
                  </Button>
                  {canShare && (
                    <Button
                      variant='outlined'
                      startIcon={<IosShareIcon />}
                      disabled={rendering}
                      onClick={share}
                    >
                      {translate('social.share')}
                    </Button>
                  )}
                  {instagram.connectedAs ? (
                    <Button
                      variant='contained'
                      startIcon={<InstagramIcon />}
                      disabled={
                        rendering ||
                        slides.length === 0 ||
                        !!phase ||
                        (kind === 'feed' && text.length > MAX_CAPTION)
                      }
                      onClick={() => setConfirming(true)}
                      data-testid='instagram-publish'
                    >
                      {translate('social.publish')}
                    </Button>
                  ) : (
                    instagram.loaded && (
                      <Button
                        startIcon={<InstagramIcon />}
                        onClick={() =>
                          document
                            .getElementById('instagram')
                            ?.scrollIntoView({ behavior: 'smooth' })
                        }
                      >
                        {translate('social.connect_first')}
                      </Button>
                    )
                  )}
                </Stack>
                {phase && (
                  <Alert
                    severity='info'
                    icon={<CircularProgress size={18} />}
                    sx={{ mt: 2 }}
                    data-testid='instagram-progress'
                  >
                    {translate(phase === 'uploading' ? 'social.uploading' : 'social.publishing')}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, lg: 5 }} sx={{ minWidth: 0 }}>
            <Card variant='outlined'>
              <CardContent>
                <Stack
                  direction='row'
                  sx={{ mb: 1, justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Typography variant='h6'>{translate('social.caption')}</Typography>
                  <Button size='small' startIcon={<ContentCopyIcon />} onClick={copyText}>
                    {translate('social.copy')}
                  </Button>
                </Stack>
                <TextField
                  multiline
                  fullWidth
                  minRows={14}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  slotProps={{ htmlInput: { 'data-testid': 'social-caption' } }}
                  helperText={translate('social.caption_hint', { count: text.length })}
                  error={text.length > MAX_CAPTION}
                />
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ mt: 1, display: 'flex', gap: 0.5 }}
                >
                  <CheckIcon fontSize='small' /> {translate('social.how_to')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Box sx={{ mt: 2 }}>
        {instagram.loaded && (
          <InstagramAccount
            account={instagram.account}
            posts={instagram.posts}
            expired={instagram.expired}
            onChanged={() => instagram.refetch()}
          />
        )}
      </Box>

      <Dialog open={confirming} onClose={() => setConfirming(false)}>
        <DialogTitle>{translate('social.confirm_title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {translate(kind === 'story' ? 'social.confirm_story' : 'social.confirm_feed', {
              smart_count: slides.length,
              username: instagram.connectedAs,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)}>{translate('social.cancel')}</Button>
          <Button variant='contained' onClick={publish} data-testid='instagram-confirm'>
            {translate('social.confirm_go')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default SocialPost
